/**
 * board-list/helpers — pure I→O derivations for the rail: the category
 * "view switch" options, the kind→label resolver, and the board/kind →
 * department grouping functions.
 *
 * Extracted VERBATIM from the former KanbanBoardList monolith during the
 * <500-line decomposition. `computeRailCategoryOptions`, `boardKindLabel`,
 * `boardDepartment`, `departmentForKind`, `groupKindsByDepartment` and
 * `groupBoardsByDepartment` are re-exported from `KanbanBoardList.tsx` so
 * existing consumers + the package barrels resolve unchanged. No behavioural
 * change.
 */
import type { Board, BoardDepartment, BoardKind } from '../../types'
import { DEPARTMENT_ORDER, KIND_TO_DEPARTMENT, LABEL_BY_KIND } from './constants'

/**
 * FEAT-2026-06-07-KANBAN-RAIL-CATEGORY-SWITCH — derive the category
 * "view switch" options for the nav rail: each board KIND that has at
 * least one visible (non-pinned, non-sub) board, in the same display
 * `order` the rail's sections use. Pure I→O so the icon-switch logic
 * is testable without mounting the rail. Mirrors the gallery's
 * `computeAvailableCategories` but stays local to avoid coupling the
 * rail to the gallery module.
 */
export function computeRailCategoryOptions(
  grouped: Partial<Record<BoardKind, Board[]>>,
  order: BoardKind[],
): { kind: BoardKind; count: number }[] {
  return order
    .map((kind) => ({ kind, count: grouped[kind]?.length ?? 0 }))
    .filter((o) => o.count > 0)
}

/** Human label for a board kind — mirrors the rail's section-header
 *  derivation (LABEL_BY_KIND override, else the humanized union key).
 *  Exported for the top-bar switcher so both surfaces label categories
 *  identically. */
export function boardKindLabel(kind: BoardKind): string {
  return LABEL_BY_KIND[kind] ?? kind.replace(/_/g, ' ')
}

/** Department for a board: prefer the backend-supplied `department`
 *  (authoritative), else derive from `kind`. Keeps the FE correct both
 *  before and after the backend department field is deployed. */
export function boardDepartment(board: { kind: BoardKind; department?: BoardDepartment | null }): BoardDepartment {
  return board.department ?? KIND_TO_DEPARTMENT[board.kind] ?? 'OPERATIONS'
}

/** Department for a bare kind (no board instance). */
export function departmentForKind(kind: BoardKind): BoardDepartment {
  return KIND_TO_DEPARTMENT[kind] ?? 'OPERATIONS'
}

/** Fold a flat kind order into business-department groups (DEPARTMENT_ORDER),
 *  keeping each kind in its original position within its department. Pure +
 *  exported so the rail, gallery and any future surface share ONE grouping.
 *  Unknown departments are appended (never hidden). */
export function groupKindsByDepartment(
  kindOrder: BoardKind[],
): Array<{ dept: BoardDepartment; kinds: BoardKind[] }> {
  const byDept = new Map<BoardDepartment, BoardKind[]>()
  for (const kind of kindOrder) {
    const d = departmentForKind(kind)
    const arr = byDept.get(d) ?? []
    arr.push(kind)
    byDept.set(d, arr)
  }
  const ordered: BoardDepartment[] = [
    ...DEPARTMENT_ORDER.filter((d) => byDept.has(d)),
    ...[...byDept.keys()].filter((d) => !DEPARTMENT_ORDER.includes(d)),
  ]
  return ordered.map((dept) => ({ dept, kinds: byDept.get(dept) ?? [] }))
}

/**
 * FEAT-2026-06-15-KANBAN-BOARD-CATEGORY-MOVE — group already-filtered
 * boards (the per-kind buckets) into department sections, honouring each
 * board's `department` override via `boardDepartment`. This is what lets
 * an operator "allocate" a CUSTOM board under any category (e.g. Finance)
 * and have it actually render under that department header, rather than
 * staying stuck in the Operations/Custom slot its kind would otherwise
 * pin it to.
 *
 * Within a department, kinds keep their `kindOrder` (BOARD_KIND_RAIL_ORDER)
 * slot and boards keep their incoming order (activity-sorted upstream). A
 * kind can therefore appear under more than one department: its derived
 * default for non-overridden boards, plus any department an override
 * points at. Pure + exported so the grouping is unit-tested apart from the
 * rail. With no overrides present it is identical to the kind-derived
 * grouping (boardDepartment falls back to KIND_TO_DEPARTMENT[kind]).
 */
export function groupBoardsByDepartment(
  groupedByKind: Partial<Record<BoardKind, Board[]>>,
  kindOrder: BoardKind[],
): Array<{ dept: BoardDepartment; kinds: Array<{ kind: BoardKind; boards: Board[] }> }> {
  const byDept = new Map<BoardDepartment, Map<BoardKind, Board[]>>()
  for (const kind of kindOrder) {
    const list = groupedByKind[kind]
    if (!list || list.length === 0) continue
    for (const board of list) {
      const dept = boardDepartment(board)
      let kindMap = byDept.get(dept)
      if (!kindMap) {
        kindMap = new Map<BoardKind, Board[]>()
        byDept.set(dept, kindMap)
      }
      const arr = kindMap.get(kind) ?? []
      arr.push(board)
      kindMap.set(kind, arr)
    }
  }
  const orderedDepts: BoardDepartment[] = [
    ...DEPARTMENT_ORDER.filter((d) => byDept.has(d)),
    ...[...byDept.keys()].filter((d) => !DEPARTMENT_ORDER.includes(d)),
  ]
  return orderedDepts.map((dept) => {
    const kindMap = byDept.get(dept) ?? new Map<BoardKind, Board[]>()
    const kinds = kindOrder
      .filter((k) => (kindMap.get(k)?.length ?? 0) > 0)
      .map((kind) => ({ kind, boards: kindMap.get(kind) ?? [] }))
    return { dept, kinds }
  })
}
